/**
 * Main Layout Component
 *
 * Provides consistent layout with header, navigation, and logout.
 */

import { ReactNode, useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../features/auth';
import { VersionHistoryModal, getLatestVersion } from './VersionHistoryModal';
import { PlanningNavMenu } from './PlanningNavMenu';

function getInitials(name: string | undefined): string {
  if (!name) return '??';
  const parts = name
    .trim()
    .split(/\s+/)
    .filter((p) => p.length > 0);
  if (parts.length === 0) return '??';
  const firstPart = parts[0];
  if (parts.length === 1 && firstPart) {
    return firstPart.substring(0, 2).toUpperCase();
  }
  const first = firstPart?.[0] ?? '';
  const lastPart = parts[parts.length - 1];
  const last = lastPart?.[0] ?? '';
  return (first + last).toUpperCase() || '??';
}

interface LayoutProps {
  children: ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const { user, logout, hasRole, isTeamManagerOrHR } = useAuth();
  const navigate = useNavigate();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [showVersionModal, setShowVersionModal] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  async function handleLogout() {
    setDropdownOpen(false);
    await logout();
    navigate('/login', { replace: true });
  }

  // Epic 5, Story 5.2: gedeelde predicaten voor de nav-sectie-scheiding
  // (visuele dividers verschijnen alleen als de erop volgende groep ook
  // daadwerkelijk iets rendert — geen hangende scheidingsstreep).
  const showsTeamOrLeidinggevende = hasRole(['beoordelaar', 'hr', 'admin']) || isTeamManagerOrHR;
  const showsBeheer = hasRole(['hr', 'admin']);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            {/* Logo / Title */}
            <Link to="/dashboard" className="flex items-center">
              <h1 className="text-xl font-semibold text-gray-900">Functieloonhuis</h1>
            </Link>

            {/* Navigation — Epic 5, Story 5.2: rol-gesectioneerde compositie in
                vaste volgorde eigen -> team -> beheer (AD-8), hasRole-JSX-
                patroon behouden (geen data-driven permissie-menu). */}
            <nav className="hidden md:flex items-center space-x-8">
              {/* Eigen-sectie (altijd) */}
              <Link
                to="/dashboard"
                className="text-gray-600 hover:text-gray-900 px-3 py-2 text-sm font-medium"
              >
                Dashboard
              </Link>
              <Link
                to="/mijn-verlof"
                className="text-gray-600 hover:text-gray-900 px-3 py-2 text-sm font-medium"
              >
                Mijn verlof
              </Link>
              <Link
                to="/mijn-rooster"
                className="text-gray-600 hover:text-gray-900 px-3 py-2 text-sm font-medium"
              >
                Mijn rooster
              </Link>
              {/* Story C-5.2 (AC 2): "Mijn indeling" — elke geauthenticeerde
                  gebruiker, altijd zichtbaar (zelfde eigen-sectie-conditie als
                  Mijn verlof/Mijn rooster hierboven, geen extra rol-check). */}
              <Link
                to="/mijn-indeling"
                className="text-gray-600 hover:text-gray-900 px-3 py-2 text-sm font-medium"
              >
                Mijn indeling
              </Link>

              {showsTeamOrLeidinggevende && (
                <span aria-hidden="true" className="h-5 border-l border-[#E2E8F0]" />
              )}

              {/* Team-sectie (additief): "Mijn Team" is bestaand,
                  ongewijzigd beoordelaar-gedrag (AC 4) — functioneel
                  gescheiden van de nieuwe leidinggevende-capability-
                  ingangen, ook al staan ze in dezelfde visuele groep. */}
              {hasRole(['beoordelaar', 'hr', 'admin']) && (
                <Link
                  to="/team"
                  className="text-gray-600 hover:text-gray-900 px-3 py-2 text-sm font-medium"
                >
                  Mijn Team
                </Link>
              )}

              {/* Leidinggevende (capability-gated, AD-4/AD-8 — zelfde bron
                  als Story 5.1's ManagerDashboardSection). hr/admin
                  kwalificeren hier via isTeamManagerOrHR (AD-4's volledige
                  requireTeamManagerOrHR-predicate), consistent met dat
                  diezelfde backend-endpoints (approval-queue,
                  attendance-calendar) hr/admin al org-breed toegang geven. */}
              {isTeamManagerOrHR && (
                <>
                  <Link
                    to="/goedkeuringen"
                    className="text-gray-600 hover:text-gray-900 px-3 py-2 text-sm font-medium"
                  >
                    Goedkeuringen
                  </Link>
                  <Link
                    to="/aanwezigheid"
                    className="text-gray-600 hover:text-gray-900 px-3 py-2 text-sm font-medium"
                  >
                    Team-aanwezigheid
                  </Link>
                  {/* Story 8.4 (besluit V39): de leesweg naar het verloop van
                      een verlofaanvraag van een teamlid. Staat naast
                      Goedkeuringen en niet erin: daar staat wat hij moet
                      beslissen, hier wat hij mag lezen. */}
                  <Link
                    to="/team/verlof"
                    className="text-gray-600 hover:text-gray-900 px-3 py-2 text-sm font-medium"
                  >
                    Team-verlof
                  </Link>
                  {/* Story C-5.2 (AC 1): "Planning" — vier tabs
                      (Planbord/Bezetting/Taken/Kwalificaties), zelfde
                      isTeamManagerOrHR-capability als Goedkeuringen/
                      Team-aanwezigheid hierboven (AD-4/AD-8). Conflicten
                      krijgen bewust géén eigen tab (C-4.3 banner+paneel). */}
                  <PlanningNavMenu />
                </>
              )}

              {showsBeheer && <span aria-hidden="true" className="h-5 border-l border-[#E2E8F0]" />}

              {/* Beheer-sectie (HR/admin, ongewijzigde gating) */}
              {showsBeheer && (
                <Link
                  to="/medewerkers"
                  className="text-gray-600 hover:text-gray-900 px-3 py-2 text-sm font-medium"
                >
                  Medewerkers
                </Link>
              )}
            </nav>

            {/* User Avatar Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center space-x-3 focus:outline-none"
                data-testid="user-menu-button"
              >
                <span className="text-sm text-gray-700">{user?.name}</span>
                <div className="w-10 h-10 rounded-full bg-[#2F5A7A] flex items-center justify-center text-white font-medium text-sm">
                  {getInitials(user?.name)}
                </div>
              </button>

              {/* Dropdown Menu */}
              {dropdownOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-lg ring-1 ring-gray-200 z-50">
                  <div className="px-4 py-3 border-b border-gray-100">
                    <p className="text-sm font-medium text-gray-900">{user?.name}</p>
                    <p className="text-xs text-gray-500 capitalize">{user?.role}</p>
                  </div>
                  <div className="py-1">
                    {/* Handleiding - All roles */}
                    <Link
                      to="/handleiding"
                      onClick={() => setDropdownOpen(false)}
                      className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 group"
                    >
                      <svg
                        className="w-4 h-4 mr-3 text-gray-400 group-hover:text-gray-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                        />
                      </svg>
                      Handleiding
                    </Link>

                    {/* HR/Admin: Export */}
                    {hasRole(['hr', 'admin']) && (
                      <Link
                        to="/export"
                        onClick={() => setDropdownOpen(false)}
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 group"
                      >
                        <svg
                          className="w-4 h-4 mr-3 text-gray-400 group-hover:text-gray-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                          />
                        </svg>
                        Export
                      </Link>
                    )}

                    {/* Admin/HR: Instellingen (Story TD-2, tech-debt-followup) */}
                    {hasRole(['admin', 'hr']) && (
                      <Link
                        to="/instellingen"
                        onClick={() => setDropdownOpen(false)}
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 group"
                      >
                        <svg
                          className="w-4 h-4 mr-3 text-gray-400 group-hover:text-gray-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                        </svg>
                        Instellingen
                      </Link>
                    )}
                  </div>
                  <div className="border-t border-gray-100">
                    <button
                      onClick={handleLogout}
                      className="flex items-center w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100 group"
                      data-testid="logout-button"
                    >
                      <svg
                        className="w-4 h-4 mr-3 text-red-400 group-hover:text-red-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                        />
                      </svg>
                      Uitloggen
                    </button>
                  </div>
                  <div className="border-t border-gray-100">
                    <button
                      onClick={() => {
                        setDropdownOpen(false);
                        setShowVersionModal(true);
                      }}
                      className="flex items-center w-full text-left px-4 py-2 text-sm text-gray-500 hover:bg-gray-100 group"
                    >
                      <svg
                        className="w-4 h-4 mr-3 text-gray-400 group-hover:text-gray-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      Versie {getLatestVersion()}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">{children}</main>

      {/* Version History Modal */}
      {showVersionModal && <VersionHistoryModal onClose={() => setShowVersionModal(false)} />}
    </div>
  );
}
